import Box from 'UIKit/Box/Box'
import { BoxProps } from 'UIKit/Box/types'
import Image from 'UIKit/Image/Image'
import Link from 'UIKit/Link/Link'
import Skeleton from 'UIKit/Skeleton/Skeleton'
import Text from 'UIKit/Text/Text'
import { RouteConfig } from 'config/constants/route'
import { UserBonus } from 'config/types/bonus/userBonus'
import React from 'react'
import { Trans } from 'react-i18next'
import { useCashbackBonus } from 'state/bonus/hooks/cashbackBonus'
import styled from 'styled-components'
import { Icons } from 'svgs'
import { useDisplayBonusName } from 'views/BonusCenter/hooks'

interface BonusItemLayoutProps extends BoxProps {
  bonus?: UserBonus
  onDismiss?: () => void
}

const BonusItemLayout: React.FC<BonusItemLayoutProps> = ({ bonus, children, onDismiss = () => {}, ...props }) => {
  const [name] = useDisplayBonusName(bonus.info?.name)
  const { activeBonus } = useCashbackBonus()

  return (
    <StyleCardItemBonus {...props}>
      {bonus.info?.badge && <StyledBonusBadge>{bonus.info?.badge}</StyledBonusBadge>}

      {bonus ? (
        <>
          {activeBonus?.id === bonus?.id ? (
            <StyledCashbackBonusImage>
              <Image src={bonus.info.image} alt="gift-header-title" width={50} height={50} />
            </StyledCashbackBonusImage>
          ) : (
            <StyledImage>
              <Image src={bonus.info.image} alt="gift-header-title" width={120} height={120} />
            </StyledImage>
          )}
          <Text
            fontSize="16px"
            fontWeight="700"
            lineHeight="18px"
            mb="16px"
            mt="2px"
            width="calc(100% - 110px)"
            style={{ zIndex: 1 }}
          >
            {name}
          </Text>
        </>
      ) : (
        <>
          <Skeleton width="160px" height="40px" mt="12px" />
          <Skeleton width="200px" height="20px" mt="20px" />
          <Skeleton width="100%" height="20px" mt="20px" />
          <Skeleton width="100%" height="20px" mt="12px" />
          <Skeleton width="100%" height="20px" mt="12px" />
          <Skeleton width="100%" height="20px" mt="12px" />
        </>
      )}

      {bonus && (
        <>
          {children}
          <Box>
            <StyledBonusCenter href={RouteConfig.BonusCenter} mt="8px" onClick={onDismiss || (() => {})}>
              <Text color="textAlt1" fontSize="12px" mr="6px">
                <Trans>Bonus Center</Trans>
              </Text>
              <Icons.ChevronRightIcon width="5px" />
            </StyledBonusCenter>
          </Box>
        </>
      )}
    </StyleCardItemBonus>
  )
}

const StyledBonusCenter = styled(Link)`
  cursor: pointer;
  justify-content: center;
  align-items: center;

  svg {
    fill: ${({ theme }) => theme.colors.textAlt1};
  }

  &:hover {
    ${Text} {
      color: ${({ theme }) => theme.colors.text} !important;
    }

    svg {
      fill: ${({ theme }) => theme.colors.text};
    }
  }
`

const StyledImage = styled(Box)`
  position: absolute;
  z-index: 0;
  top: 0px;
  right: 0px;
  overflow: hidden;

  img {
    min-width: 80px;
    min-height: 80px;
  }
  border-top-right-radius: ${({ theme }) => theme.radii.default};

  ${({ theme }) => theme.mediaQueries.sm} {
    border-top-right-radius: ${({ theme }) => theme.radii.tiny};
  }
`

const StyledCashbackBonusImage = styled(StyledImage)`
  img {
    min-width: unset;
    min-height: unset;
  }
`

const StyleCardItemBonus = styled(Box)`
  position: relative;

  border-radius: ${({ theme }) => theme.radii.default};
  padding: 20px 15px;

  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;

  border: 1px solid #21242d;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0) 65.91%),
    radial-gradient(at 70% 44%, hsl(297deg 97% 73%) -180%, transparent 33%),
    radial-gradient(at 35% 74%, hsl(211deg 96% 63%) -180%, transparent 35%);

  ${({ theme }) => theme.mediaQueries.sm} {
    border-radius: ${({ theme }) => theme.radii.tiny};
  }
`

const StyledBonusBadge = styled(Text)`
  width: fit-content;
  font-size: 12px;
  font-weight: 500;
  position: absolute;
  padding: 5px;
  padding-right: 15px;
  background: linear-gradient(252.02deg, #7f00ff -3.62%, rgba(225, 0, 255, 0.8) 124.68%);
  border-radius: 2px;
  top: -4px;
  left: 0;
  clip-path: polygon(0 0, 100% 0, 95% 80%, 0 100%);
`

export default BonusItemLayout
